import React, { useEffect } from 'react';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAnalytics } from '../../hooks/useAnalytics';

const CompletionStep = ({ userName, onboardingData, onCreateAgent, onSkip, isSubmitting }) => {
  const { trackStepStart, trackFieldInteraction } = useOnboarding();
  const { trackButtonClick, trackPageView } = useAnalytics();
  
  // Track step start on mount
  useEffect(() => {
    trackStepStart(4, 'completion');
    trackPageView('onboarding_completion', {
      user_completed_onboarding: true,
      onboarding_data_summary: {
        has_company_name: !!onboardingData.companyName,
        has_industry: !!onboardingData.industry,
        has_use_case: !!onboardingData.primaryUseCase,
        goals_count: onboardingData.mainGoals ? onboardingData.mainGoals.length : 0
      }
    });
  }, [trackStepStart, trackPageView, onboardingData]);

  /**
   * Handle create agent button click with analytics
   */
  const handleCreateAgent = () => {
    // Track decision point
    trackFieldInteraction(4, 'onboarding_exit_choice', 'selected', {
      choice: 'create_agent',
      user_name: userName,
      onboarding_summary: {
        company_name: onboardingData.companyName,
        industry: onboardingData.industry,
        use_case: onboardingData.primaryUseCase,
        goals: onboardingData.mainGoals
      }
    });
    
    trackButtonClick('create_first_agent', 'onboarding_completion', {
      choice: 'create_agent',
      onboarding_completed: true
    });
    
    onCreateAgent();
  };

  /**
   * Handle skip button click with analytics
   */
  const handleSkip = () => {
    // Track decision point
    trackFieldInteraction(4, 'onboarding_exit_choice', 'selected', {
      choice: 'skip_to_dashboard',
      user_name: userName,
      onboarding_summary: {
        company_name: onboardingData.companyName,
        industry: onboardingData.industry,
        use_case: onboardingData.primaryUseCase,
        goals: onboardingData.mainGoals
      }
    });
    
    trackButtonClick('skip_to_dashboard', 'onboarding_completion', {
      choice: 'skip_to_dashboard',
      onboarding_completed: true
    });
    
    onSkip();
  };

  /*
  const getRecommendations = () => {
    const { primaryUseCases, technicalLevel } = onboardingData;
    const recommendations = [];

    // Based on use cases
    if (primaryUseCases?.includes('Customer Support Automation')) {
      recommendations.push({
        title: 'Customer Support Agent',
        description: 'Start with a 24/7 customer support agent to handle common inquiries and escalate complex issues.',
        icon: '🎧'
      });
    }

    if (primaryUseCases?.includes('Lead Generation & Qualification')) {
      recommendations.push({
        title: 'Lead Qualification Agent',
        description: 'Automatically qualify leads and schedule demos with your sales team.',
        icon: '🎯'
      });
    }

    if (primaryUseCases?.includes('Appointment Scheduling')) {
      recommendations.push({
        title: 'Scheduling Assistant',
        description: 'Let AI handle appointment booking and calendar management seamlessly.',
        icon: '📅'
      });
    }

    // Default recommendations if no specific use cases
    if (recommendations.length === 0) {
      recommendations.push({
        title: 'General Business Assistant',
        description: 'A versatile AI agent that can handle customer inquiries, basic support, and information gathering.',
        icon: '🤖'
      });
    }

    // Add technical level recommendation
    if (technicalLevel === 'non-technical') {
      recommendations.push({
        title: 'No-Code Setup',
        description: 'We recommend starting with our pre-built templates that require zero coding experience.',
        icon: '🚀'
      });
    }

    return recommendations.slice(0, 3); // Show max 3 recommendations
  };
  */

  return (
    <div className="space-y-8">
      {/* Next Steps */}
      <div className="max-w-2xl mx-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-card-foreground mb-4">
            What's Next?
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                1
              </div>
              <div>
                <p className="font-medium text-card-foreground">Create your first AI agent</p>
                <p className="text-sm text-muted-foreground">Use our guided setup to build an agent tailored to your needs</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                2
              </div>
              <div>
                <p className="font-medium text-card-foreground">Talk to the agent</p>
                <p className="text-sm text-muted-foreground">Use our playground to test conversations and optimize responses</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                3
              </div>
              <div>
                <p className="font-medium text-card-foreground">Go live</p>
                <p className="text-sm text-muted-foreground">Deploy your agent and start engaging with customers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
        <Button
          onClick={handleSkip}
          variant="outline"
          className="px-6"
          disabled={isSubmitting}
        >
          Skip
        </Button>
        
        <Button
          onClick={handleCreateAgent}
          className="px-6"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            'Setting up...'
          ) : (
            <>
              Create Your First Agent
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default CompletionStep;